import React from 'react';
import {
  STATUS_CHIP_MAP,
  formatJobSalary,
  getJobOptionLabel,
  formatDateToDotOnly,
  formatDateToDotWithTime,
  getRemainingTime,
  BaseButton,
  useIsMobile,
  useLang,
  useT,
} from '@repo/common';
import JobCard from '../../JobCard';
import DetailView from '../../Detail/DetailView';
import { buildLanguageSkills } from '../../Detail/Content/JobOverview';

const DetailContent = ({ job, optionMeta }) => {
  const t = useT();
  const { lang } = useLang();
  const isMobile = useIsMobile();
  const { status, createdAt, modifiedAt, applicationDeadline } = job;
  const statusChip = STATUS_CHIP_MAP[status];

  const deadlineRows = [
    { label: t('Created date', '등록일'), value: formatDateToDotWithTime(createdAt) },
    { label: t('Last updated', '최근 수정'), value: formatDateToDotWithTime(modifiedAt) },
    { label: t('Apply by', '마감일'), value: formatDateToDotOnly(applicationDeadline) },
    { label: t('Time left', '남은 기간'), value: getRemainingTime(applicationDeadline) },
  ];

  const requirementRows = [
    {
      label: t('Location', '거주지'),
      value: job.koreaResidentsOnly
        ? t('Korea residents only', '국내 거주자만')
        : t('Overseas applicants accepted', '해외 거주자 지원 가능'),
    },
    {
      label: t('Language skills', '언어 능력'),
      value:
        buildLanguageSkills(job, optionMeta, lang, t) ||
        t('Not specified', '미지정'),
    },
    {
      label: t('Nationality', '국적'),
      value:
        getJobOptionLabel(optionMeta, job.nationalityOptionItemId, '') ||
        job.nationalityCustomText ||
        t('No preference', '무관'),
    },
    {
      label: t('Career level', '경력 수준'),
      value: getJobOptionLabel(optionMeta, job.careerLevelOptionItemId),
    },
    {
      label: t('Salary', '급여'),
      value: formatJobSalary(job, optionMeta),
    },
  ];

  /* 모바일: 흰 섹션 2개(요약 / 상세 미리보기)를 16px 간격으로 쌓고, 카드 래퍼 없이 바로 그린다 */
  if (isMobile) {
    const sectionTitle = (title, caption) => (
      <div className='flex w-full items-center gap-1.5 whitespace-nowrap pb-2'>
        <span className='typo-b16Strong text-text-primary'>{title}</span>
        {caption && (
          <span className='typo-b11 text-text-placeholder'>{caption}</span>
        )}
      </div>
    );
    const infoRows = (rows, labelWidth) => (
      <div className='flex w-full flex-col gap-2 text-text-primary'>
        {rows.map(({ label, value }) => (
          <div key={label} className='flex w-full items-start'>
            <div className={`typo-b12Strong shrink-0 ${labelWidth}`}>
              {label}
            </div>
            <div className='min-w-0 flex-[1_0_0] break-words typo-b12'>
              {value || '-'}
            </div>
          </div>
        ))}
      </div>
    );
    const caption = t('As seen by candidates', '지원자에게 보이는 화면');

    return (
      <div className='flex w-full max-w-[868px] flex-col items-start gap-4'>
        <div className='flex w-full flex-col items-start gap-[30px] bg-grayscale-gray00 px-4 pb-10 pt-4'>
          <div className='flex items-center gap-2'>
            <span className='typo-b12Strong text-text-secondary'>
              {t('Status:', '상태:')}
            </span>
            {statusChip && (
              <span
                className={`inline-flex h-6 items-center justify-center rounded-lg px-1.5 py-1 typo-b12Strong ${
                  statusChip.className || ''
                }`}
              >
                {statusChip.label}
              </span>
            )}
          </div>

          <div className='flex w-full flex-col items-start'>
            {sectionTitle(t('Feed View', '피드 보기'), caption)}
            <div className='pointer-events-none w-full'>
              <JobCard job={job} />
            </div>
          </div>

          <div className='flex w-full flex-col items-start'>
            {sectionTitle(t('Application deadline', '지원 마감'))}
            {infoRows(deadlineRows, 'w-[100px]')}
          </div>

          <div className='flex w-full flex-col items-start'>
            {sectionTitle(t('Requirements', '지원 자격'))}
            {infoRows(requirementRows, 'w-[100px]')}
          </div>
        </div>

        <div className='flex w-full flex-col items-start gap-[30px] bg-grayscale-gray00 px-4 pb-10 pt-4'>
          {sectionTitle(t('Detail preview', '상세 미리보기'), caption)}
          <div className='pointer-events-none w-full'>
            <DetailView isAdmin job={job} optionMeta={optionMeta} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col items-start justify-center gap-10 max-w-[868px] w-[868px]'>
      <div className='flex items-center gap-2'>
        <span className='text-text-secondary typo-b16Strong'>
          {t('Status:', '상태:')}
        </span>
        {statusChip && (
          <BaseButton
            type='text'
            size='s'
            hideIcon
            bgclass={`px-1.5 py-1 h-6 border-none ${
              statusChip.className || ''
            }`}
            textclass='typo-b12Strong'
          >
            {statusChip.label}
          </BaseButton>
        )}
      </div>

      <div className='flex flex-col items-start w-full'>
        <div className='flex items-center gap-2 text-text-secondary pb-2'>
          <span className='typo-b18Strong'>{t('Feed View', '피드 보기')}</span>
          <span className='typo-b13'>
            {t('As seen by candidates', '지원자에게 보이는 화면')}
          </span>
        </div>

        <div className='flex flex-col items-start py-2 rounded-lg bg-grayscale-gray00 w-full pointer-events-none'>
          <JobCard job={job} />
        </div>
      </div>

      <div className='flex flex-col items-start w-full'>
        <span className='typo-b18Strong text-text-secondary pb-2'>
          {t('Application deadline', '지원 마감')}
        </span>

        <div className='flex flex-col items-start py-2 rounded-lg bg-grayscale-gray00 w-full pointer-events-none gap-2'>
          {deadlineRows.map(({ label, value }) => (
            <div key={label} className='flex text-text-primary'>
              <div className='typo-b12Strong w-[100px] shrink-0'>{label}</div>
              <div className='typo-b12'>{value || '-'}</div>
            </div>
          ))}
        </div>
      </div>

      <div className='flex flex-col items-start w-full'>
        <span className='typo-b18Strong text-text-secondary pb-2'>
          {t('Requirements', '지원 자격')}
        </span>

        <div className='flex flex-col items-start py-2 rounded-lg bg-grayscale-gray00 w-full pointer-events-none gap-2'>
          {requirementRows.map(({ label, value }) => (
            <div key={label} className='flex text-text-primary'>
              <div className='typo-b12Strong w-[100px] shrink-0'>{label}</div>
              <div className='typo-b12'>{value || '-'}</div>
            </div>
          ))}
        </div>
      </div>

      <div className='flex flex-col items-start w-full'>
        <div className='flex items-center gap-2 text-text-secondary pb-2'>
          <span className='typo-b18Strong'>
            {t('Detail preview', '상세 미리보기')}
          </span>
          <span className='typo-b13'>
            {t('As seen by candidates', '지원자에게 보이는 화면')}
          </span>
        </div>

        <div className='flex flex-col items-start py-2 rounded-lg bg-grayscale-gray00 w-full pointer-events-none'>
          <DetailView isAdmin job={job} optionMeta={optionMeta} />
        </div>
      </div>
    </div>
  );
};

export default DetailContent;
